import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles, Loader2, BookOpen, Trophy, CheckCircle2, XCircle, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { sanitizeHtml } from "../lib/sanitize";
import { toast } from "sonner";

/**
 * Onboarding flow — optional, opt-in. Shows on first login but the user can
 * always decline. Can be re-opened from the help button.
 *
 * Flow phases:
 *   invite       → "Möchtest du eine geführte Tour?"  [Ja / Nein, vielleicht später]
 *   tour-N       → step through 5 handbook sections, then continue to quiz
 *   quiz-intro   → "Bist du bereit für ein kurzes Quiz?"
 *   quiz-N       → one multiple-choice question per step (5 total)
 *   result       → show score + per-question feedback
 *
 * Props:
 *   open      : boolean
 *   onClose() : skip / close, marks onboarding_completed (so it doesn't auto-show again)
 *   onComplete(): user fully finished tour + quiz
 */

const ROLE_LABEL = { verkaufer: "Vertriebler", manager: "Manager", direktor: "Direktor" };
const normalizeRole = (r) => {
  if (r === "admin") return "direktor";
  if (r === "trainer") return "manager";
  if (r === "manager" || r === "direktor" || r === "verkaufer") return r;
  return "verkaufer";
};

const OnboardingModal = ({ open, onClose, onComplete }) => {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);

  const [phase, setPhase] = useState("invite");
  const [tourStep, setTourStep] = useState(0);
  const [quizStep, setQuizStep] = useState(0);
  const [tour, setTour] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const scrollRef = useRef(null);

  // Reset scroll to top whenever we advance to a new tour or quiz step
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [tourStep, quizStep, phase]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase("invite");
      setTourStep(0);
      setQuizStep(0);
      setAnswers({});
      setResult(null);
    }
  }, [open]);

  const startTour = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/handbook/${role}/onboarding`);
      setTour(data.tour || []);
      setQuestions(data.questions || []);
      setPhase("tour");
      setTourStep(0);
    } catch (e) {
      toast.error("Tour konnte nicht geladen werden");
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const declineTour = () => {
    onClose?.();
  };

  const nextTour = () => {
    if (tourStep < tour.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      setPhase("quiz-intro");
    }
  };
  const prevTour = () => tourStep > 0 && setTourStep(tourStep - 1);

  const startQuiz = () => {
    setPhase("quiz");
    setQuizStep(0);
  };

  const skipQuiz = () => {
    // Even without quiz, finishing the tour counts as onboarding done
    onComplete?.();
  };

  const selectAnswer = (qId, idx) => {
    setAnswers({ ...answers, [qId]: idx });
  };

  const nextQuiz = async () => {
    if (quizStep < questions.length - 1) {
      setQuizStep(quizStep + 1);
      return;
    }
    // Submit
    setLoading(true);
    try {
      const { data } = await api.post(`/handbook/${role}/onboarding/submit`, { answers });
      setResult(data);
      setPhase("result");
    } catch (e) {
      toast.error("Auswertung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    onComplete?.();
  };

  const downloadPdf = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${baseUrl}/api/handbook/${role}.pdf`, "_blank", "noopener");
  };

  const currentQuestion = useMemo(() => questions[quizStep], [questions, quizStep]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="onboarding-modal"
    >
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-hidden nv-card flex flex-col">
        {/* Close (X) — always visible, allows the user to skip out at any time */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full text-novatus-text-3 hover:text-white hover:bg-white/5"
          aria-label="Schließen"
          data-testid="onboarding-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ── INVITE PHASE ── */}
        {phase === "invite" && (
          <div className="px-8 py-10 text-center" data-testid="onboarding-invite">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-novatus-green/15 mb-4">
              <Sparkles className="w-8 h-8 text-novatus-green" />
            </div>
            <span className="label-eyebrow">Willkommen in der Academy</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-3 mb-3">
              Möchtest du eine geführte Tour?
            </h2>
            <p className="text-novatus-text-2 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Wir führen dich in 5 kurzen Schritten durch dein {ROLE_LABEL[role] || "Vertriebler"}-Handbuch
              und beenden mit einem kleinen Quiz, damit du die wichtigsten Punkte verinnerlichst.
              Dauer: ca. 5 Minuten. Optional — du kannst auch ablehnen.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <button
                onClick={startTour}
                disabled={loading}
                className="bg-novatus-green hover:bg-[#25E058] text-black font-bold rounded-lg px-6 py-3 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="onboarding-accept-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Ja, los geht's
              </button>
              <button
                onClick={declineTour}
                className="bg-novatus-card-2 hover:bg-white/5 border border-novatus text-novatus-text-2 hover:text-white rounded-lg px-6 py-3 transition-colors"
                data-testid="onboarding-decline-btn"
              >
                Nein, vielleicht später
              </button>
            </div>
            <p className="text-xs text-novatus-text-3 mt-5">
              Du kannst die Tour jederzeit über den Hilfe-Button (?) erneut starten.
            </p>
          </div>
        )}

        {/* ── TOUR PHASE ── */}
        {phase === "tour" && tour[tourStep] && (
          <div className="flex flex-col flex-1 min-h-0" data-testid={`onboarding-tour-step-${tourStep}`}>
            <div className="px-6 pt-6 pb-3 border-b border-novatus shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-novatus-green" />
                <span className="label-eyebrow">
                  Schritt {tourStep + 1} von {tour.length} · {ROLE_LABEL[role]}-Handbuch
                </span>
              </div>
              {/* Progress dots */}
              <div className="flex gap-1.5 mt-3">
                {tour.map((step, i) => (
                  <div
                    key={`tour-dot-${step.id ?? i}`}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      i <= tourStep ? "bg-novatus-green" : "bg-novatus-card-2"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-6 py-5 handbook-content text-novatus-text-2"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(tour[tourStep].html) }}
            />
            <div className="px-6 py-4 border-t border-novatus shrink-0 flex items-center justify-between gap-3 bg-novatus-bg">
              <button
                onClick={prevTour}
                disabled={tourStep === 0}
                className="text-sm text-novatus-text-2 hover:text-white inline-flex items-center gap-1 disabled:opacity-30"
                data-testid="onboarding-tour-prev-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück
              </button>
              <button
                onClick={nextTour}
                className="bg-novatus-green hover:bg-[#25E058] text-black font-bold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2"
                data-testid="onboarding-tour-next-btn"
              >
                {tourStep === tour.length - 1 ? "Weiter zum Quiz" : "Weiter"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── QUIZ INTRO ── */}
        {phase === "quiz-intro" && (
          <div className="px-8 py-10 text-center" data-testid="onboarding-quiz-intro">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-novatus-green/15 mb-4">
              <Trophy className="w-8 h-8 text-novatus-green" />
            </div>
            <span className="label-eyebrow">Letzter Schritt</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-3 mb-3">
              Kurzes Quiz — {questions.length} Fragen
            </h2>
            <p className="text-novatus-text-2 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Damit du das Gelesene direkt verankerst, kommen jetzt {questions.length} Multiple-Choice-Fragen.
              Pro Frage 1 richtige Antwort. Du bestehst, wenn du mindestens {Math.max(1, questions.length - 1)} richtig hast.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <button
                onClick={startQuiz}
                className="bg-novatus-green hover:bg-[#25E058] text-black font-bold rounded-lg px-6 py-3 inline-flex items-center justify-center gap-2"
                data-testid="onboarding-quiz-start-btn"
              >
                <ArrowRight className="w-4 h-4" /> Quiz starten
              </button>
              <button
                onClick={skipQuiz}
                className="bg-novatus-card-2 hover:bg-white/5 border border-novatus text-novatus-text-2 hover:text-white rounded-lg px-6 py-3"
                data-testid="onboarding-quiz-skip-btn"
              >
                Quiz überspringen, Onboarding abschließen
              </button>
            </div>
          </div>
        )}

        {/* ── QUIZ PHASE ── */}
        {phase === "quiz" && currentQuestion && (
          <div className="flex flex-col flex-1 min-h-0" data-testid={`onboarding-quiz-step-${quizStep}`}>
            <div className="px-6 pt-6 pb-3 border-b border-novatus shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-4 h-4 text-novatus-green" />
                <span className="label-eyebrow">
                  Frage {quizStep + 1} von {questions.length}
                </span>
              </div>
              <div className="flex gap-1.5 mt-3">
                {questions.map((q, i) => (
                  <div
                    key={`quiz-dot-${q.id ?? i}`}
                    className={`h-1.5 flex-1 rounded-full ${
                      i <= quizStep ? "bg-novatus-green" : "bg-novatus-card-2"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
              <h3 className="text-lg font-display font-bold text-white mb-5">{currentQuestion.text}</h3>
              <div className="space-y-2.5">
                {currentQuestion.options.map((opt, idx) => {
                  const selected = answers[currentQuestion.id] === idx;
                  return (
                    <button
                      key={`q-${currentQuestion.id}-opt-${idx}`}
                      onClick={() => selectAnswer(currentQuestion.id, idx)}
                      className={`w-full text-left p-3.5 rounded-lg border transition-all ${
                        selected
                          ? "border-novatus-green bg-novatus-green/10 text-white"
                          : "border-novatus bg-novatus-card-2 text-novatus-text-2 hover:border-novatus-green/40 hover:text-white"
                      }`}
                      data-testid={`quiz-option-${quizStep}-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center ${
                            selected ? "border-novatus-green bg-novatus-green" : "border-novatus-text-3"
                          }`}
                        >
                          {selected && <div className="w-2 h-2 rounded-full bg-black" />}
                        </div>
                        <span className="text-sm leading-relaxed">{opt}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-novatus shrink-0 flex items-center justify-between gap-3 bg-novatus-bg">
              <button
                onClick={() => quizStep > 0 && setQuizStep(quizStep - 1)}
                disabled={quizStep === 0}
                className="text-sm text-novatus-text-2 hover:text-white inline-flex items-center gap-1 disabled:opacity-30"
                data-testid="onboarding-quiz-prev-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück
              </button>
              <button
                onClick={nextQuiz}
                disabled={answers[currentQuestion.id] === undefined || loading}
                className="bg-novatus-green hover:bg-[#25E058] text-black font-bold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-quiz-next-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : quizStep === questions.length - 1 ? (
                  <>Auswerten <Trophy className="w-4 h-4" /></>
                ) : (
                  <>Weiter <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── RESULT ── */}
        {phase === "result" && result && (
          <div className="flex flex-col flex-1 min-h-0" data-testid="onboarding-result">
            <div className="px-6 pt-6 pb-4 border-b border-novatus shrink-0 text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${result.passed ? "bg-novatus-green/15" : "bg-amber-400/15"}`}>
                {result.passed ? (
                  <Trophy className="w-8 h-8 text-novatus-green" />
                ) : (
                  <Sparkles className="w-8 h-8 text-amber-400" />
                )}
              </div>
              <h2 className="font-display font-black text-3xl text-white">
                {result.score} / {result.total} richtig
              </h2>
              <p className="text-novatus-text-2 text-sm mt-2">
                {result.passed
                  ? "Hervorragend — du hast das Wesentliche verstanden. Viel Erfolg im Training!"
                  : "Schau dir gleich die unten markierten Fragen nochmal an. Du kannst die Tour jederzeit über das Hilfe-Menü wiederholen."}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5">
              {result.results.map((r, i) => {
                const q = questions.find((x) => x.id === r.id);
                return (
                  <div
                    key={r.id}
                    className={`p-3 rounded-lg border ${r.is_correct ? "border-novatus-green/40 bg-novatus-green/5" : "border-amber-400/40 bg-amber-400/5"}`}
                    data-testid={`result-q-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      {r.is_correct ? (
                        <CheckCircle2 className="w-5 h-5 text-novatus-green shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{q?.text}</p>
                        {!r.is_correct && (
                          <p className="text-xs text-novatus-text-2 mt-1.5">
                            <span className="text-novatus-green font-semibold">Richtig wäre gewesen:</span>{" "}
                            {q?.options[r.correct_index]}
                          </p>
                        )}
                        {r.explanation && (
                          <p className="text-xs text-novatus-text-3 mt-1.5 italic">{r.explanation}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-novatus shrink-0 flex flex-col sm:flex-row items-center gap-3 bg-novatus-bg">
              <button
                onClick={downloadPdf}
                className="w-full sm:w-auto bg-novatus-card-2 hover:bg-white/5 border border-novatus-green/50 text-white rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
                data-testid="result-download-pdf-btn"
              >
                <FileText className="w-3.5 h-3.5 text-novatus-green" />
                Handbuch als PDF
              </button>
              <button
                onClick={finish}
                className="w-full sm:flex-1 bg-novatus-green hover:bg-[#25E058] text-black font-bold rounded-lg px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2"
                data-testid="result-finish-btn"
              >
                Onboarding abschließen <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;
